
import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { PhoneScreeningState } from "./types";

export function useCallMonitoring() {
  const callStatusIntervalRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (callStatusIntervalRef.current) {
        window.clearInterval(callStatusIntervalRef.current);
      }
    };
  }, []);

  const pollCallStatus = async (
    callId: string, 
    setState: (state: (prev: PhoneScreeningState) => PhoneScreeningState) => void,
    simulatedLinesRef: React.MutableRefObject<string[]>
  ) => {
    if (callStatusIntervalRef.current) {
      window.clearInterval(callStatusIntervalRef.current);
    }

    callStatusIntervalRef.current = window.setInterval(async () => {
      try {
        const { data, error } = await supabase.functions.invoke('twilio-integration', {
          body: { action: 'getCallStatus', callId }
        });

        if (error) throw new Error(error.message);

        console.log("Call status update:", data);

        if (data.status === 'completed' || data.status === 'failed' || data.status === 'busy' || data.status === 'no-answer') {
          if (callStatusIntervalRef.current) {
            window.clearInterval(callStatusIntervalRef.current);
            callStatusIntervalRef.current = null;
          }

          if (data.status === 'completed') {
            const fullTranscript = simulatedLinesRef.current.join("\n");
            const answersSummary = generateSimulatedSummary();

            setState(() => ({
              status: 'completed',
              transcript: fullTranscript,
              liveTranscript: null,
              summary: answersSummary,
              callId
            }));

            toast.success("Phone screening completed successfully");
          } else {
            setState(prev => ({
              ...prev,
              status: 'failed',
              liveTranscript: `Call ended with status: ${data.status}`
            }));

            toast.error(`Call failed: ${data.status}`);
          }
        } else if (data.status === 'in-progress') {
          setState(prev => ({
            ...prev,
            status: 'in-progress'
          }));
        }
      } catch (err) {
        console.error("Error polling call status:", err);
      }
    }, 5000);
  };

  return {
    pollCallStatus,
    callStatusIntervalRef
  };
}

const generateSimulatedSummary = () => ({
  strengths: [
    "3 years of React experience",
    "Skilled with hooks, context API and Redux",
    "Experience optimizing React apps for performance",
    "Good testing practices using Jest and React Testing Library",
    "Problem-solving abilities demonstrated through real-time dashboard optimization"
  ],
  weaknesses: [
    "Backend experience seems more limited than frontend",
    "Limited GraphQL experience mentioned",
    "No mention of TypeScript or strong typing systems",
  ],
  skillMatches: [
    { skill: "React", proficiency: 8 },
    { skill: "JavaScript", proficiency: 8 },
    { skill: "Testing", proficiency: 7 },
    { skill: "Node.js", proficiency: 6 },
    { skill: "Problem Solving", proficiency: 8 }
  ],
  culture: "The candidate appears articulate and professional. Their approach to testing and problem-solving suggests they value quality and performance, which aligns with our engineering culture.",
  availability: "Available to start in two weeks",
  compensation: "Expecting $100,000-$120,000 annually, flexible based on benefits package",
  overallRecommendation: "yes"
});
